import { useState } from "react";
import { useTranslation } from "react-i18next";
import { PlusOutlined } from "@ant-design/icons";
import { Flex } from "antd";

import { useFetchSecurityGroups } from "@av/apps/common-apps/platform/services/admin-settings/security-groups";
import type {
  AdminSettingsUserBase,
  SecurityGroup,
} from "@av/apps/common-apps/platform/services/admin-settings/type";
import { App } from "@av/components/atoms/app/App";
import { Button } from "@av/components/atoms/Button";
import { Form } from "@av/components/atoms/form/Form";
import { Tabs } from "@av/components/molecules/tabs/Tabs";
import { UserFilters } from "./components/UserFilters";
import { UserTable } from "./components/UserTable";
import {
  useFetchAtsAdminSettingsUsers,
  useUpdateBulkUserPermission,
  useUpdateUserPermission,
} from "../../../../services/admin-settings/admin-settings";
import { filterAtsAdminSettingsUsers } from "../../../hooks/filter-admin-settings-users";
import { type AssignUserFormValues, AssignUserModal } from "../common/assign-modal/AssignUserModal";
import {
  type AssignBulkUserFormValues,
  BulkAssignUserModal,
} from "../common/assign-modal/BulkAssignUserModal";

// eslint-disable-next-line import/no-default-export
export default function RolesAndPermissionsSettings() {
  const { t } = useTranslation();

  const [searchText, setSearchText] = useState("");
  const [activeTab, setActiveTab] = useState("byUsers");
  const { data: users = [], isLoading } = useFetchAtsAdminSettingsUsers();
  const { message } = App.useApp();

  const [selectedSecurityGroups, setSelectedSecurityGroups] = useState<string[] | null>(null);
  const [isAssignmentDialogOpen, setIsAssignmentDialogOpen] = useState(false);
  const [isBulkAssignmentDialogOpen, setIsBulkAssignmentDialogOpen] = useState(false);
  const {
    mutate: updateUser,
    error,
    isPending: isUserAssignmentSubmitting,
  } = useUpdateUserPermission("ats");
  const {
    mutate: updateBulkUsers,
    error: bulkError,
    isPending: isBulkAssignmentSubmitting,
  } = useUpdateBulkUserPermission("ats");

  const { data: securityGroups } = useFetchSecurityGroups("ats");

  const [form] = Form.useForm<AssignUserFormValues>();
  const [bulkForm] = Form.useForm<AssignBulkUserFormValues>();
  const userOptions = users
    .filter((user) => user.atsUserProvisioningPending !== true)
    .map((user: AdminSettingsUserBase) => ({
      label: user.name,
      value: user.userId,
    }));

  const filteredUsers = filterAtsAdminSettingsUsers({
    users,
    searchText,
    selectedSecurityGroups,
  });

  const handleEditingSelectedBulkUser = function (formValues: AssignBulkUserFormValues) {
    updateBulkUsers(
      { users: formValues.users, securityGroups: formValues.securityGroups ?? [] },
      {
        onSuccess: () => {
          message.success(t("platform.adminSettings.users_actionMessage_updateSuccess"));
          setIsBulkAssignmentDialogOpen(false);
        },
        onError: () => {
          message.error(t("platform.adminSettings.users_actionMessage_updateFailure"));
        },
      }
    );
  };

  const handleEditingSelectedUser = function (user: AdminSettingsUserBase) {
    form.setFieldValue("userId", user.userId);
    form.setFieldValue("email", user.email);
    form.setFieldValue(
      "securityGroups",
      user.securityGroups.map((group: SecurityGroup) => {
        return group.id;
      })
    );
    form.setFieldValue("name", user.name);
    setIsAssignmentDialogOpen(true);
  };

  const handleUpdatingUser = function (formValues: AssignUserFormValues) {
    updateUser(
      {
        ...formValues,
        securityGroups: formValues.securityGroups ?? [],
      },
      {
        onSuccess: () => {
          message.success(t("platform.adminSettings.users_actionMessage_updateSuccess"));
          setIsAssignmentDialogOpen(false);
        },
        onError: () => {
          message.error(t("platform.adminSettings.users_actionMessage_updateFailure"));
        },
      }
    );
  };

  return (
    <>
      <Tabs
        defaultTab="byUsers"
        activeTab={activeTab}
        onChange={setActiveTab}
        tabs={[
          {
            key: "byUsers",
            label: (
              <div data-testid="org-setup-users-tab">
                {t("platform.adminSettings.tabTitle_users")}
              </div>
            ),
            children: (
              <Flex justify="space-between" vertical gap="large">
                <Flex justify="space-between">
                  <UserFilters
                    searchText={searchText}
                    selectedSecurityGroups={selectedSecurityGroups}
                    onSearchTextChange={setSearchText}
                    onSelectedSecurityGroupsChange={setSelectedSecurityGroups}
                  />
                  <Button
                    type="link"
                    testIdPrefix="admin-settings-edit-bulk"
                    onClick={() => setIsBulkAssignmentDialogOpen(true)}
                  >
                    <PlusOutlined />
                    {t("platform.adminSettings.users_button_editBulk")}
                  </Button>
                </Flex>
                <UserTable
                  users={filteredUsers}
                  isLoading={isLoading}
                  isEditUserPending={(user) => user.atsUserProvisioningPending === true}
                  onEditUser={handleEditingSelectedUser}
                  testIdPrefix="ats-admin-settings-users"
                  t={t}
                />
              </Flex>
            ),
          },
        ]}
        testIdPrefix="admin-settings-section"
      />
      <BulkAssignUserModal
        open={isBulkAssignmentDialogOpen}
        securityGroupOptions={securityGroups}
        userOptions={userOptions}
        onCancel={() => setIsBulkAssignmentDialogOpen(false)}
        onSubmit={handleEditingSelectedBulkUser}
        form={bulkForm}
        serverError={bulkError}
        isSubmitting={isBulkAssignmentSubmitting}
      />

      <AssignUserModal
        open={isAssignmentDialogOpen}
        form={form}
        serverError={error}
        securityGroupOptions={securityGroups}
        onCancel={() => setIsAssignmentDialogOpen(false)}
        onSubmit={handleUpdatingUser}
        isSubmitting={isUserAssignmentSubmitting}
      />
    </>
  );
}
